import React from "react";
import { Gift, Sparkles, Star } from "lucide-react";

// Playful, kid-facing treasure card. Each completed visit fills a star; once the
// child hits the threshold the chest "unlocks" with a celebratory glow.
export function TreasureChest({ child }) {
  const t = child.treasure || { count: 0, threshold: 5, eligible: false };
  const filled = Math.min(t.count, t.threshold);
  const stars = Array.from({ length: t.threshold });

  return (
    <div
      data-testid={`treasure-chest-${child.id}`}
      className={`relative overflow-hidden rounded-2xl p-7 transition-all duration-500 ${
        t.eligible
          ? "bg-gradient-to-br from-brass via-[#d8a24a] to-[#b9812f] shadow-[0_18px_50px_-12px_rgba(185,129,47,0.6)]"
          : "bg-gradient-to-br from-[#3a2f28] to-espresso"
      }`}
    >
      {t.eligible && (
        <>
          <Sparkles className="absolute top-4 right-5 text-ivory/90 animate-pulse" size={22} />
          <Sparkles className="absolute bottom-6 left-6 text-ivory/70 animate-pulse" size={16} style={{ animationDelay: "0.4s" }} />
        </>
      )}

      <div className="flex items-center gap-4">
        <div
          className={`grid place-items-center h-16 w-16 rounded-2xl shrink-0 ${
            t.eligible ? "bg-ivory/25 animate-bounce" : "bg-ivory/10"
          }`}
        >
          <Gift className={t.eligible ? "text-ivory" : "text-brass"} size={30} strokeWidth={1.6} />
        </div>
        <div>
          <div className={`font-serif text-2xl ${t.eligible ? "text-ivory" : "text-ivory"}`}>{child.name}</div>
          <div className={`text-xs uppercase tracking-[0.16em] ${t.eligible ? "text-ivory/80" : "text-ivory/45"}`}>
            {t.eligible ? "Treasure unlocked!" : `${t.count} of ${t.threshold} visits`}
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2" data-testid={`treasure-stars-${child.id}`}>
        {stars.map((_, i) => (
          <Star
            key={i}
            size={26}
            strokeWidth={1.5}
            className={`transition-all duration-500 ${i < filled ? "scale-110" : "scale-90"}`}
            style={{
              fill: i < filled ? "#fff" : "transparent",
              color: i < filled ? "#fff" : t.eligible ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.25)",
              transitionDelay: `${i * 70}ms`,
            }}
          />
        ))}
      </div>

      <p className={`mt-5 text-sm leading-relaxed ${t.eligible ? "text-ivory/90" : "text-ivory/55"}`}>
        {t.eligible
          ? "Woohoo! Ask the front desk to open the treasure chest on your next visit. 🎉".replace(" 🎉", "")
          : `${Math.max(t.threshold - t.count, 0)} more visit${t.threshold - t.count === 1 ? "" : "s"} until a treasure!`}
      </p>
    </div>
  );
}
